/**
 * Замеры метрик канала: публикатор → бэкенд. Миграция 0012, docs/telegram.md §12.
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ МАРШРУТ, А НЕ ПОЛЕ В ОТЧЁТЕ О ПОПЫТКЕ. Замер приходит не тогда, когда
 * публикуется пост. Подписчиков сборщик считает по своему расписанию, реакции прилетают
 * апдейтом через час после публикации, комментарии — через день. Привязать это к отчёту о
 * попытке значило бы либо держать попытку открытой сутками, либо потерять всё, что пришло
 * после её закрытия.
 *
 * СКОУП — `dispatch:report`, тот же, что у отчётов о попытке, отзыве и разрешении. Новый
 * скоуп потребовал бы перевыпуска токена публикатора на дроплете; замер по своей природе
 * ровно то же самое, что остальные отчёты этого скоупа — наблюдение, о котором публикатор
 * сообщает и на основании которого ничего не решает.
 *
 * ПАЧКОЙ, А НЕ ПО ОДНОМУ. Один тик сборщика приносит замер на бренд, один опрос апдейтов —
 * пачку реакций; отдельный HTTP-запрос на каждое число превратил бы штатную работу в
 * десятки запросов в минуту. Потолок пачки — `CHANNEL_METRIC_BATCH_MAX`.
 *
 * ПОЧЕМУ ЗАМЕР НЕСЁТ СВОЁ ВРЕМЯ, А НЕ ПРИНИМАЕТ ВРЕМЯ БЭКЕНДА. Между наблюдением и
 * доставкой лежит спул: замер, снятый до обрыва связи, доедет минутами позже, и записать
 * его временем приёма значило бы сдвинуть точку ряда. Ряд замеров И ЕСТЬ метрика —
 * сдвигать его нельзя.
 */

import * as z from 'zod';
import { BRAND_RE } from '../approval-key/limits.ts';
import { UUID_V7_RE } from '../ids.ts';
import { ISO_UTC_MS } from '../time.ts';

const Uuid = z.string().regex(UUID_V7_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);

/**
 * Метрики, у которых есть источник. Просмотров здесь нет и не будет: у Bot API нет ни
 * метода, ни апдейта, отдающего счётчик просмотров поста, а чужие контуры (MTProto-сессия
 * человека, платный агрегатор) решением от 04.09.2026 отклонены. Экономии времени здесь
 * нет по другой причине — ей нужна не выборка из API, а согласованная база сравнения.
 */
export const CHANNEL_METRICS = ['subscribers', 'reactions', 'comments'] as const;
export const ChannelMetricSchema = z.enum(CHANNEL_METRICS);
export type ChannelMetric = z.output<typeof ChannelMetricSchema>;

/**
 * Откуда взято число. Разбор расхождения начинается именно с этого вопроса, поэтому
 * источник хранится, а не выводится из метрики: у реакций их два.
 *
 *   get_chat_member_count — периодический опрос `getChatMemberCount` (подписчики);
 *   reaction_count_update — апдейт `message_reaction_count` (реакции);
 *   discussion_reply      — подсчёт ответов в треде группы обсуждения (комментарии);
 *   publication           — базовый НОЛЬ, поставленный посту сразу после публикации.
 *
 * Последний существует ради различия «ноль» и «прочерк»: пост без единой реакции обязан
 * показывать 0, а не «источника нет». Ставится только тем метрикам, наблюдение за
 * которыми уже включено, — комментариям до появления группы обсуждения не ставится
 * никогда, иначе экран утверждал бы «комментариев ноль» там, где комментировать негде.
 */
export const CHANNEL_METRIC_SOURCES = [
  'get_chat_member_count',
  'reaction_count_update',
  'discussion_reply',
  'publication',
] as const;
export const ChannelMetricSourceSchema = z.enum(CHANNEL_METRIC_SOURCES);
export type ChannelMetricSource = z.output<typeof ChannelMetricSourceSchema>;

/** Потолок пачки. Больше сотни замеров за раз означает не работу, а поломку сборщика. */
export const CHANNEL_METRIC_BATCH_MAX = 100;

/**
 * Один замер. `telegram_message_id` отсутствует у метрики КАНАЛА и обязателен у метрики
 * ПОСТА — соответствие проверяется здесь, а не только CHECK'ом базы: отбить бессмыслицу
 * на границе дешевле, чем разбирать 500 от SQLite.
 */
export const ChannelMetricSampleSchema = z
  .strictObject({
    brand: z.string().regex(BRAND_RE),
    metric: ChannelMetricSchema,
    /** Номер поста в канале. `null` — замер относится к каналу целиком. */
    telegram_message_id: z.int().positive().nullable(),
    /** Счётчик, не приращение: апдейты Telegram несут полное значение. */
    value: z.int().nonnegative(),
    /** Момент НАБЛЮДЕНИЯ, а не доставки. */
    sampled_at: IsoUtcMs,
    source: ChannelMetricSourceSchema,
  })
  .refine((s) => (s.metric === 'subscribers') === (s.telegram_message_id === null), {
    message:
      'подписчики замеряются у канала (telegram_message_id = null), ' +
      'реакции и комментарии — у поста (telegram_message_id задан)',
    path: ['telegram_message_id'],
  });
export type ChannelMetricSample = z.output<typeof ChannelMetricSampleSchema>;

/** Тело `POST /dispatch/v1/channel-metrics`. */
export const ChannelMetricsBodySchema = z.strictObject({
  samples: ChannelMetricSampleSchema.array().min(1).max(CHANNEL_METRIC_BATCH_MAX),
});
export type ChannelMetricsBody = z.output<typeof ChannelMetricsBodySchema>;

/**
 * Ответ. `accepted` и `duplicates` разделены намеренно: повтор после оборванного ответа —
 * штатное поведение спула, и он обязан выглядеть успехом, а не ошибкой. Но сборщик,
 * который шлёт ТОЛЬКО дубли, сломан, и увидеть это можно лишь по разнице двух чисел.
 */
export const ChannelMetricsResultSchema = z.strictObject({
  accepted: z.int().nonnegative(),
  duplicates: z.int().nonnegative(),
});
export type ChannelMetricsResult = z.output<typeof ChannelMetricsResultSchema>;

/** Строка `v_ops_channel_metrics_weekly` — форма ответа `GET /api/v1/ops/channel-metrics-weekly`. */
export const ChannelMetricsWeeklyRowSchema = z.strictObject({
  week_start: z.string(),
  brand_code: z.string(),
  posts: z.int().nonnegative(),
  /** `null` — на этой неделе подписчиков не замеряли. Не ноль: ноль был бы утверждением. */
  subscribers: z.int().nonnegative().nullable(),
  reactions: z.int().nonnegative().nullable(),
  reaction_posts: z.int().nonnegative(),
  comments: z.int().nonnegative().nullable(),
  comment_posts: z.int().nonnegative(),
});
export type ChannelMetricsWeeklyRow = z.output<typeof ChannelMetricsWeeklyRowSchema>;

/** Идентификатор замера в базе — UUIDv7, как у всех строк. Схема нужна тестам границы. */
export const ChannelMetricSampleIdSchema = Uuid;
